/**
 * CastButton - Chromecast support for video streaming
 * Works with YouTube, RTMP/HLS, and VCloud embeds
 * Uses Google Cast SDK for casting to TVs and screens
 */
import { useState, useEffect, useCallback } from 'react';
import { Cast, CastConnected, Monitor, Tv } from 'lucide-react';

// Custom icons since lucide doesn't have Cast icons
const CastIcon = ({ className, connected }) => (
  <svg 
    className={className} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M2 8V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-6" />
    <path d="M2 12a9 9 0 0 1 8 8" />
    <path d="M2 16a5 5 0 0 1 4 4" />
    <circle cx="2" cy="20" r="1" fill={connected ? "currentColor" : "none"} />
  </svg>
);

export const CastButton = ({ 
  videoUrl, 
  videoType = 'video/mp4',
  title = 'Stream',
  thumbnailUrl,
  className = ''
}) => {
  const [castAvailable, setCastAvailable] = useState(false);
  const [isCasting, setIsCasting] = useState(false);
  const [castSession, setCastSession] = useState(null);
  const [deviceName, setDeviceName] = useState('');

  // Initialize Cast SDK
  useEffect(() => {
    // Check if Cast SDK is already loaded
    if (window.chrome?.cast) {
      initializeCast();
      return;
    }

    // Load Google Cast SDK
    const script = document.createElement('script');
    script.src = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
    script.async = true;
    
    script.onload = () => {
      // Wait for Cast SDK to initialize
      window['__onGCastApiAvailable'] = function(isAvailable) {
        if (isAvailable) {
          initializeCast();
        }
      };
    };
    
    document.head.appendChild(script);

    return () => {
      // Cleanup
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const initializeCast = useCallback(() => {
    try {
      const cast = window.cast;
      const chrome = window.chrome;
      
      if (!cast?.framework || !chrome?.cast) {
        console.log('Cast framework not available');
        return;
      }

      const context = cast.framework.CastContext.getInstance();
      
      context.setOptions({
        receiverApplicationId: chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID,
        autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED
      });

      // Listen for session state changes
      context.addEventListener(
        cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
        (event) => {
          const session = context.getCurrentSession();
          
          switch (event.sessionState) {
            case cast.framework.SessionState.SESSION_STARTED:
            case cast.framework.SessionState.SESSION_RESUMED:
              setIsCasting(true);
              setCastSession(session);
              setDeviceName(session?.getCastDevice()?.friendlyName || 'Device');
              break;
            case cast.framework.SessionState.SESSION_ENDED:
            case cast.framework.SessionState.NO_SESSION:
              setIsCasting(false);
              setCastSession(null);
              setDeviceName('');
              break;
          }
        }
      );

      setCastAvailable(true);
    } catch (error) {
      console.error('Error initializing Cast:', error);
      setCastAvailable(false);
    }
  }, []);

  // Start casting
  const startCasting = async () => {
    if (!window.cast?.framework) {
      alert('Chromecast is not available. Please ensure you have a Cast-compatible device on your network.');
      return;
    }

    try {
      const context = window.cast.framework.CastContext.getInstance();
      
      // Request session (shows device picker)
      await context.requestSession();
      
      const session = context.getCurrentSession();
      if (session && videoUrl) {
        await loadMedia(session);
      }
    } catch (error) {
      if (error.code === 'cancel') {
        // User cancelled - do nothing
      } else {
        console.error('Error starting cast:', error);
        alert('Unable to connect to Chromecast. Please try again.');
      }
    }
  };

  // Load media to the cast device
  const loadMedia = async (session) => {
    if (!session || !videoUrl) return;

    const chrome = window.chrome;
    const mediaInfo = new chrome.cast.media.MediaInfo(videoUrl, videoType);
    
    mediaInfo.metadata = new chrome.cast.media.GenericMediaMetadata();
    mediaInfo.metadata.title = title;
    
    if (thumbnailUrl) {
      mediaInfo.metadata.images = [{ url: thumbnailUrl }];
    }

    // Handle YouTube URLs
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      // For YouTube, we use a different approach
      const videoId = extractYouTubeId(videoUrl);
      if (videoId) {
        // Cast YouTube using the YouTube receiver
        mediaInfo.contentId = videoId;
        mediaInfo.contentType = 'video/mp4';
      }
    }

    const request = new chrome.cast.media.LoadRequest(mediaInfo);
    request.autoplay = true;

    try {
      await session.loadMedia(request);
      setIsCasting(true);
    } catch (error) {
      console.error('Error loading media:', error);
      // Try alternative approach
      if (error.code === chrome.cast.ErrorCode.LOAD_MEDIA_FAILED) {
        alert('This video format may not be supported for casting. Try a different video or format.');
      }
    }
  };

  // Stop casting
  const stopCasting = () => {
    try {
      const context = window.cast?.framework?.CastContext?.getInstance();
      const session = context?.getCurrentSession();
      
      if (session) {
        session.endSession(true);
      }
      
      setIsCasting(false);
      setCastSession(null);
      setDeviceName('');
    } catch (error) {
      console.error('Error stopping cast:', error);
    }
  };

  // Extract YouTube video ID
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=))([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  // Don't render if cast is not available
  if (!castAvailable) {
    return null;
  }

  return (
    <button
      onClick={isCasting ? stopCasting : startCasting}
      className={`flex items-center gap-2 px-3 py-2 rounded transition-all ${
        isCasting 
          ? 'bg-blue-500 text-white hover:bg-blue-600' 
          : 'bg-zinc-800 text-white hover:bg-zinc-700'
      } ${className}`}
      title={isCasting ? `Casting to ${deviceName}` : 'Cast to TV'}
      data-testid="cast-button"
    >
      <CastIcon className="w-5 h-5" connected={isCasting} />
      {isCasting ? (
        <span className="text-sm font-medium">
          {deviceName || 'Connected'}
        </span>
      ) : (
        <span className="text-sm font-medium">Cast</span>
      )}
    </button>
  );
};

// Multi-view casting support
export const MultiViewCastButton = ({
  videoUrls = [],
  titles = [],
  className = ''
}) => {
  const [castAvailable, setCastAvailable] = useState(false);
  const [isCasting, setIsCasting] = useState(false);

  useEffect(() => {
    // Check for cast availability
    const checkCast = () => {
      if (window.chrome?.cast && window.cast?.framework) {
        setCastAvailable(true);
      }
    };

    // Check immediately and after a delay
    checkCast();
    const timeout = setTimeout(checkCast, 2000);

    return () => clearTimeout(timeout);
  }, []);

  const handleCast = () => {
    if (videoUrls.length === 0) {
      alert('No videos available to cast.');
      return;
    }

    if (videoUrls.length === 1) {
      // Single video - use regular cast
      const event = new CustomEvent('startCast', { detail: { url: videoUrls[0], title: titles[0] } });
      window.dispatchEvent(event);
    } else {
      // Multiple videos - show warning
      alert('Multi-view casting is not fully supported on all devices. For best results, cast a single stream or use a compatible smart TV app.');
    }
  };

  if (!castAvailable) return null;

  return (
    <button
      onClick={handleCast}
      className={`flex items-center gap-2 px-3 py-2 bg-zinc-800 text-white rounded hover:bg-zinc-700 transition-all ${className}`}
      title="Cast to TV"
      data-testid="multiview-cast-button"
    >
      <Tv className="w-5 h-5" />
      <span className="text-sm font-medium">Cast to TV</span>
    </button>
  );
};

export default CastButton;
